import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { toast } from 'react-toastify';
import { X } from 'lucide-react';
import { useContractServices } from '../../context/ContractServicesContext';

/**
 * A modal component for staking CEL tokens in a project
 */
const StakeProjectModal = ({ 
  project, 
  isOpen, 
  onClose, 
  getExplorerUrl,
  onSuccess
}) => {
  // Contract services
  const { services, executeContractTransaction, isReady } = useContractServices();

  // Form state
  const [stakeAmount, setStakeAmount] = useState("");
  const [lockDuration, setLockDuration] = useState("30"); // Default to 30 days
  const [stakingParams, setStakingParams] = useState(null);
  const [userCelBalance, setUserCelBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);

  // Fetch staking parameters and user's CEL balance when modal opens
  useEffect(() => {
    if (isOpen && isReady && project) {
      fetchStakingParams();
      fetchUserCelBalance();
    }
  }, [isOpen, isReady, project]);

  // Function to fetch staking parameters
  const fetchStakingParams = async () => {
    if (!isReady || !services?.projectStaking) return;
    
    try {
      const params = await services.projectStaking.getStakingParameters();
      console.log('Staking parameters:', params);
      setStakingParams(params);
      
      // Set default lock duration to the minimum days or 30 days, whichever is greater
      const minDays = params.minLockDays || 7;
      const defaultDuration = Math.max(minDays, 30);
      
      // Ensure default duration is valid (between min and max)
      const maxDays = params.maxLockDays || 730;
      const validDefaultDuration = Math.min(Math.max(defaultDuration, minDays), maxDays);
      
      console.log(`Setting initial lock duration to ${validDefaultDuration} days (min: ${minDays}, max: ${maxDays})`);
      setLockDuration(validDefaultDuration.toString());
    } catch (error) {
      console.error('Error fetching staking parameters:', error);
    }
  };

  // Function to fetch user's CEL token balance
  const fetchUserCelBalance = async () => {
    if (!isReady || !services?.celToken) return;
    
    try {
      setLoadingBalance(true);
      // Get signer's address
      const address = await services.celToken.contract.signer.getAddress();
      
      // Get CEL balance
      const balance = await services.celToken.balanceOf(address);
      setUserCelBalance(ethers.utils.formatEther(balance));
    } catch (error) {
      console.error("Error fetching CEL balance:", error);
    } finally {
      setLoadingBalance(false);
    }
  };

  // Get the projectId from the project object
  const getProjectId = () => {
    if (project?.transactionData?.projectId) {
      return parseInt(project.transactionData.projectId);
    } else if (project?.projectId) {
      return parseInt(project.projectId);
    } else if (project?.projectTokenId) {
      return parseInt(project.projectTokenId);
    }
    
    throw new Error("Invalid project ID. Please ensure the project has a valid blockchain ID.");
  };

  // Handle stake amount input change
  const handleStakeAmountChange = (e) => {
    setStakeAmount(e.target.value);
  };

  // Handle lock duration input change
  const handleLockDurationChange = (e) => {
    // Allow only numeric values
    const value = e.target.value.replace(/[^0-9]/g, '');
    setLockDuration(value);
  };

  // Set max stake amount based on user's CEL balance
  const setMaxAmount = () => {
    if (userCelBalance) {
      setStakeAmount(userCelBalance);
    }
  };

  // Calculate unlock date
  const calculateUnlockDate = (durationDays) => {
    const unlockDate = new Date();
    unlockDate.setDate(unlockDate.getDate() + parseInt(durationDays));
    return unlockDate.toLocaleDateString('en-US', {
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };

  // Submit stake transaction
  const submitStake = async () => {
    if (!isReady) {
      toast.error("Smart contract services are not ready. Please connect your wallet and try again.");
      return;
    }
    
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) {
      toast.error("Please enter a valid stake amount");
      return;
    }
    
    // Check against user's balance
    if (userCelBalance && parseFloat(stakeAmount) > parseFloat(userCelBalance)) {
      toast.error(`Insufficient balance. You have ${parseFloat(userCelBalance).toFixed(4)} CEL but trying to stake ${parseFloat(stakeAmount).toFixed(4)} CEL.`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Validate lock duration against contract bounds
      const lockDurationInt = parseInt(lockDuration);
      
      // Default bounds based on contract (if we couldn't fetch stakingParams)
      const minDays = stakingParams?.minLockDays || 7;
      const maxDays = stakingParams?.maxLockDays || 730;
      
      console.log(`Validating lock duration: ${lockDurationInt} days (allowed range: ${minDays}-${maxDays} days)`);
      
      if (isNaN(lockDurationInt) || lockDurationInt < minDays || lockDurationInt > maxDays) {
        toast.error(
          <div>
            <p className="font-bold">Invalid Lock Duration</p>
            <p className="text-sm">Lock duration must be between {minDays} and {maxDays} days.</p>
            <p className="text-sm mt-1">Current value: {lockDurationInt} days</p>
          </div>
        );
        return;
      }
    
      // Get the project ID
      const projectId = getProjectId();
      
      // Show a toast indicating the process may take two steps
      toast.info(
        <div>
          <p className="font-bold">Staking Process Started</p>
          <p className="text-sm mt-1">This is a two-step process:</p>
          <ol className="text-sm mt-1 list-decimal list-inside">
            <li>Token approval (if needed)</li>
            <li>Stake transaction</li>
          </ol>
          <p className="text-xs mt-2 text-blue-600">Please confirm each transaction in your wallet when prompted.</p>
        </div>,
        { autoClose: 10000, position: "top-center" }
      );
      
      // Convert stake amount to wei
      const amountInWei = ethers.utils.parseEther(stakeAmount.toString());
      
      console.log(`Staking ${stakeAmount} CEL for project ${projectId} with lock duration ${lockDuration} days`);
      
      // Execute the blockchain transaction
      const result = await executeContractTransaction(
        services.projectStaking.stake.bind(services.projectStaking),
        [projectId, amountInWei, parseInt(lockDuration)],
        {
          pendingMessage: 'Processing stake transaction...',
          successMessage: 'Stake transaction submitted successfully!',
          failMessage: 'Failed to stake tokens',
          showToast: false // Don't show default toast, we'll handle it
        }
      );
      
      // Log the full transaction result for debugging
      console.log("Full stake transaction result:", result);
      
      // If transaction was successful, update the DB and refresh project data
      const txHash = result?.transactionHash || result?.hash;
      
      if (txHash) {
        console.log(`Transaction successful with hash: ${txHash}`);
        
        // Toast success with details about the transaction
        toast.success(
          <div>
            <p className="font-bold">Staking Successful!</p>
            <p className="text-sm mt-1">You have staked {stakeAmount} CEL for {lockDuration} days</p>
            <p className="text-xs mt-1">Tokens will unlock on {calculateUnlockDate(lockDuration)}</p>
            <div className="text-xs mt-2">
              <a 
                href={getExplorerUrl(txHash)}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                View transaction
              </a>
            </div>
          </div>,
          { autoClose: 8000, position: "top-center" }
        );
        
        // Close modal and reset form
        onClose();
        setStakeAmount("");
        
        // Call success callback to update DB and refresh data
        if (onSuccess) {
          // Convert days to seconds for database storage
          const lockDurationInSeconds = parseInt(lockDuration) * 24 * 60 * 60;
          
          const stakeData = {
            transactionHash: txHash,
            lockDuration: lockDurationInSeconds,
            stakedAmount: parseFloat(stakeAmount),
            unlockTime: Math.floor(Date.now() / 1000) + lockDurationInSeconds
          };
          
          onSuccess(txHash, stakeData);
        }
      } else {
        console.error("Transaction result missing hash:", result);
        toast.error("Staking transaction may have failed. Please check your wallet and refresh the page.");
      }
    } catch (error) {
      console.error("Staking error:", error);
      
      // Check for specific error types for better user feedback
      let errorMessage = error.message || 'Failed to stake tokens';
      
      if (errorMessage.includes("user rejected") || errorMessage.includes("User denied")) {
        toast.error("Transaction was rejected. Please try again and approve the transaction in your wallet.");
      } else if (errorMessage.includes("insufficient funds") || errorMessage.includes("Insufficient")) {
        toast.error("You don't have enough CEL tokens to complete this transaction.");
      } else if (errorMessage.includes("approve") || errorMessage.includes("allowance")) {
        toast.error("Token approval failed. Please try again and approve the transaction in your wallet.");
      } else {
        toast.error(`Staking failed: ${errorMessage}`);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white shadow-xl rounded-lg max-w-md w-full p-6 dark:bg-gray-800">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold dark:text-white">Stake in Project</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
          >
            <X size={24} />
          </button>
        </div>

        <div className="mb-6">
          {stakingParams ? (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm">
              <h4 className="font-semibold mb-1 dark:text-white">Staking Information</h4>
              <p className="dark:text-gray-300">
                Minimum Lock Period: {stakingParams.minLockDays} days
              </p>
              <p className="dark:text-gray-300">
                Maximum Lock Period: {stakingParams.maxLockDays} days
              </p>
              <p className="mt-2 text-xs text-blue-700 dark:text-blue-300">
                Longer lock periods provide higher staking rewards!
              </p>
            </div>
          ) : null}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Stake Amount (CEL)
            </label>
            <div className="relative">
              <input
                type="number"
                value={stakeAmount}
                onChange={handleStakeAmountChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Enter amount to stake"
                min="0"
                step="0.01"
              />
              {userCelBalance && (
                <button
                  type="button"
                  onClick={setMaxAmount}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-100 hover:bg-blue-200 text-blue-800 text-xs font-medium px-2 py-1 rounded"
                >
                  Max
                </button>
              )}
            </div>
            {userCelBalance !== null ? (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 flex justify-between">
                <span>Your balance: {parseFloat(userCelBalance).toFixed(4)} CEL</span>
                {stakeAmount && parseFloat(stakeAmount) > parseFloat(userCelBalance) && (
                  <span className="text-red-500">Insufficient balance</span>
                )}
              </div>
            ) : loadingBalance ? (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Loading balance...
              </div>
            ) : null}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lock Duration (Days)
            </label>
            <div className="relative">
              <input
                type="number"
                value={lockDuration}
                onChange={handleLockDurationChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder="Enter lock duration in days"
                min={stakingParams ? stakingParams.minLockDays : 7}
                max={stakingParams ? stakingParams.maxLockDays : 730}
              />
            </div>
            <div className="flex flex-col md:flex-row md:justify-between text-xs mt-1">
              <p className="text-gray-500 dark:text-gray-400">
                Longer lock duration = higher rewards
              </p>
              {stakingParams && (
                <p className="text-gray-500 dark:text-gray-400">
                  Valid range: {stakingParams.minLockDays} - {stakingParams.maxLockDays} days
                </p>
              )}
            </div>
            
            {/* Quick selection buttons */}
            {stakingParams && (
              <div className="flex flex-wrap gap-2 mt-2">
                {/* Min duration */}
                <button
                  type="button"
                  onClick={() => setLockDuration(stakingParams.minLockDays.toString())}
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Min ({stakingParams.minLockDays}d)
                </button>
                
                {/* Show common durations only if they're within range */}
                {stakingParams.minLockDays <= 30 && stakingParams.maxLockDays >= 30 && (
                  <button
                    type="button"
                    onClick={() => setLockDuration("30")}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    30 days
                  </button>
                )}
                
                {stakingParams.minLockDays <= 90 && stakingParams.maxLockDays >= 90 && (
                  <button
                    type="button"
                    onClick={() => setLockDuration("90")}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    90 days
                  </button>
                )}
                
                {stakingParams.minLockDays <= 180 && stakingParams.maxLockDays >= 180 && (
                  <button
                    type="button"
                    onClick={() => setLockDuration("180")}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    180 days
                  </button>
                )}
                
                {stakingParams.minLockDays <= 365 && stakingParams.maxLockDays >= 365 && (
                  <button
                    type="button"
                    onClick={() => setLockDuration("365")}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                  >
                    1 year
                  </button>
                )}
                
                {/* Max duration */}
                <button
                  type="button"
                  onClick={() => setLockDuration(stakingParams.maxLockDays.toString())}
                  className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                >
                  Max ({stakingParams.maxLockDays}d)
                </button>
              </div>
            )}

            {/* Validation error message */}
            {lockDuration && stakingParams && (
              parseInt(lockDuration) < stakingParams.minLockDays || 
              parseInt(lockDuration) > stakingParams.maxLockDays
            ) && (
              <p className="text-xs text-red-500 mt-1">
                Lock duration must be between {stakingParams.minLockDays} and {stakingParams.maxLockDays} days
              </p>
            )}
          </div>

          {/* Staking Preview */}
          {stakeAmount && parseFloat(stakeAmount) > 0 && (
            <div className="mb-6 p-3 bg-gray-50 dark:bg-gray-700 rounded">
              <h4 className="font-semibold mb-2 text-sm dark:text-white">Staking Preview</h4>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-300">Amount:</span>
                <span className="font-medium dark:text-white">{stakeAmount} CEL</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-300">Lock period:</span>
                <span className="font-medium dark:text-white">{lockDuration} days</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-300">Unlocks on:</span>
                <span className="font-medium dark:text-white">{calculateUnlockDate(lockDuration)}</span>
              </div>
              
              {/* Show reward multiplier if we have valid staking params and lock duration */}
              {stakingParams && lockDuration && parseInt(lockDuration) >= stakingParams.minLockDays && parseInt(lockDuration) <= stakingParams.maxLockDays && (
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                  <span className="text-gray-600 dark:text-gray-300">Reward Multiplier:</span>
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {(() => {
                      // Calculate duration factor (0 to 1 based on how close to max duration)
                      const durationRange = stakingParams.maxLockDays - stakingParams.minLockDays;
                      const durationAboveMin = parseInt(lockDuration) - stakingParams.minLockDays;
                      const durationFactor = durationRange > 0 ? durationAboveMin / durationRange : 0;
                      
                      // Calculate multiplier based on contract formula (simplified for UI)
                      const baseMultiplier = 1;
                      const bonusMultiplier = durationFactor * (stakingParams.multiplierFactor / stakingParams.scorePrecision || 2);
                      const totalMultiplier = baseMultiplier + bonusMultiplier;
                      
                      return `${totalMultiplier.toFixed(2)}x`;
                    })()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          <button
            onClick={submitStake}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
            disabled={
              loading || 
              !stakeAmount || 
              parseFloat(stakeAmount) <= 0 ||
              (userCelBalance && parseFloat(stakeAmount) > parseFloat(userCelBalance)) ||
              (!lockDuration || 
               (stakingParams && (
                 parseInt(lockDuration) < stakingParams.minLockDays || 
                 parseInt(lockDuration) > stakingParams.maxLockDays
               )))
            }
          >
            {loading ? 'Processing...' : 'Confirm Stake'}
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition duration-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default StakeProjectModal; 